import { FormSource } from '@formio/react'
import { Typography, Box, Button } from '@mui/material'
import { useState, useEffect, useMemo } from 'react'
import { useMutation } from '@cedarjs/web'
import { gql } from '@apollo/client'
import { toast } from 'react-hot-toast'
import IsolatedFormBuilder from 'src/components/IsolatedFormBuilder/IsolatedFormBuilder'
import SaveIcon from '@mui/icons-material/Save'
import { FormTemplate } from 'types/graphql'

const UPDATE_FORM_TEMPLATE_MUTATION = gql`
  mutation UpdateFormTemplate($id: String!, $input: UpdateFormTemplateInput!) {
    updateFormTemplate(id: $id, input: $input) {
      id
      formSchema
      updatedAt
    }
  }
`

interface EditFormTabProps {
  formTemplate: FormTemplate
  onUpdate: () => void
  onUnsavedChanges?: (hasUnsavedChanges: boolean) => void
}

const EditFormTab = ({
  formTemplate,
  onUpdate,
  onUnsavedChanges,
}: EditFormTabProps) => {
  const [form, setForm] = useState<FormSource | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [initialForm, setInitialForm] = useState<FormSource | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const [updateFormTemplate] = useMutation(UPDATE_FORM_TEMPLATE_MUTATION)

  const formBuilderOptions = useMemo(
    () => ({
      prohibitedElements:
        formTemplate.visibility === 'PUBLIC' ? ['digitalsignature'] : [],
    }),
    [formTemplate.visibility]
  )

  // Track changes to form
  useEffect(() => {
    if (form && initialForm) {
      const hasChanges = JSON.stringify(form) !== JSON.stringify(initialForm)
      setHasUnsavedChanges(hasChanges)
      onUnsavedChanges?.(hasChanges)
    }
  }, [form, initialForm, onUnsavedChanges])

  const handleSave = async () => {
    if (!hasUnsavedChanges || !form) return

    setIsSaving(true)
    try {
      await updateFormTemplate({
        variables: {
          id: formTemplate.id,
          input: {
            formSchema: form,
          },
        },
      })
      setInitialForm(form)
      setHasUnsavedChanges(false)
      onUnsavedChanges?.(false)
      toast.success('Form saved successfully!')
      onUpdate()
    } catch (error) {
      console.error('Error saving form:', error)
      toast.error('Failed to save form changes')
    } finally {
      setIsSaving(false)
    }
  }

  // Initialize form when formTemplate loads
  useEffect(() => {
    if (formTemplate?.formSchema) {
      setForm(formTemplate.formSchema as FormSource)
      setInitialForm(formTemplate.formSchema as FormSource)
      setHasUnsavedChanges(false)
      onUnsavedChanges?.(false)
    }
  }, [formTemplate, onUnsavedChanges])

  return (
    <div className="flex-grow py-1">
      <div className="h-full">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant="h6">Form Builder</Typography>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasUnsavedChanges || isSaving}
            startIcon={<SaveIcon />}
            size="small"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Design your form by dragging and dropping components. Remember to save
          your changes.
        </Typography>

        {form !== null && (
          <IsolatedFormBuilder
            currentForm={form}
            onChange={setForm}
            options={formBuilderOptions}
          />
        )}
      </div>
    </div>
  )
}

export default EditFormTab
